<template>
  <WdsAlert :error="!comp.completed" :data-test="computedDataTest">
    <WdsStack gap-sm align-center class="tw-flex-wrap lg:tw-flex-nowrap tw-justify-between">
      <WdsStack gap-sm align-start class="tw-flex-1">
        <WdsStack align-start>
          <WdsIcon :name="WdsIconNameEnum.WARNING" :size="20" weight="thin" class="tw-mt-0.5" />
        </WdsStack>

        <WdsStack direction-column>
          <WdsText large>
            <span class="tw-text-red-600">{{ $t(comp.title) }}</span>
          </WdsText>

          <WdsText>
            <span class="tw-text-gray-800 tw-whitespace-pre-line">{{ $t(comp.description) }}</span>
          </WdsText>
        </WdsStack>
      </WdsStack>

      <HireComplianceAction
        v-if="!!comp.action && comp.actor !== ComplianceActorTypes.Worksome"
        :action="comp.action"
        :hire-id="hireId"
        :compliance-name="comp.name"
        @open-modal="openModal"
      />
    </WdsStack>
  </WdsAlert>

  <!-- Dynamic compliance modal -->
  <component
    :is="modalComponent"
    v-if="modalComponent"
    v-model:open="isModalOpen"
    :hire-id="hireId"
  />
</template>

<script lang="ts" setup>
import kebabCase from 'lodash/kebabCase'
import { computed, ref } from 'vue'
import { WdsAlert, WdsIcon, WdsIconNameEnum, WdsStack, WdsText } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import HireComplianceAction from '@/components/Hire/HirePageCompliance/HireComplianceAction.vue'
import { ComplianceActorTypes, ComplianceAlertFragment } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { getComplianceModal } from '@/components/Hire/HirePageCompliance/modals/complianceModals'

const ComplianceAlert = graphql(`
  fragment ComplianceAlert on Compliance {
    title
    actor
    description
    applicable
    completed
    name
    action {
      ...ComplianceActionAlertButton
      inputIdentifier
    }
  }
`)

const props = defineProps<{
  compliance: FragmentType<typeof ComplianceAlert> | ComplianceAlertFragment
  hireId: string
  dataTest?: string
}>()

const comp = computed(() =>
  useFragment(ComplianceAlert, props.compliance as FragmentType<typeof ComplianceAlert>),
)

const computedDataTest = computed(
  () => props.dataTest ?? kebabCase(`action-${comp.value?.title ?? ''}`),
)

const isModalOpen = ref(false)

const modalComponent = computed(() =>
  getComplianceModal(comp.value.name, comp.value.action?.inputIdentifier),
)

const openModal = () => {
  isModalOpen.value = true
}
</script>
